import { Reveal, SectionLabel, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Trophy, ExternalLink } from "lucide-react";

const VERIFY_URL = "/reports/richard-akpan-ncl-2022.pdf";

const heroStats = [
  { k: "99th", v: "PERCENTILE" },
  { k: "68 / 3,926", v: "TEAMS_NATIONALLY" },
  { k: "TOP 1.7%", v: "OF_ALL_TEAMS" },
];

const categories = [
  { name: "Password Cracking", rank: "99th", acc: "100.0%", accNum: 100 },
  { name: "Web App Exploitation", rank: "95th", acc: "100.0%", accNum: 100 },
  { name: "Cryptography", rank: "218th", acc: "81.8%", accNum: 81.8 },
  { name: "Network Traffic Analysis", rank: "53rd", acc: "70.4%", accNum: 70.4 },
  { name: "Forensics", rank: "86th", acc: "62.5%", accNum: 62.5 },
  { name: "Scanning", rank: "94th", acc: "60.0%", accNum: 60 },
  { name: "Log Analysis", rank: "57th", acc: "52.9%", accNum: 52.9 },
  { name: "Open Source Intel", rank: "129th", acc: "48.1%", accNum: 48.1 },
  { name: "Enumeration & Exploitation", rank: "235th", acc: "40.0%", accNum: 40 },
];

export default function FieldWork() {
  return (
    <section
      id="field-work"
      data-testid="field-work-section"
      className="relative py-24 lg:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel id="SYS_05">FIELD_WORK // COMPETITIONS</SectionLabel>

        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66]">
                <Trophy size={14} strokeWidth={1.5} />
                NCL_FALL_2022 // TEAM_SFCollege
              </div>
              <h2 className="font-['Unbounded'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F5F5F5] leading-[1.05] max-w-3xl">
                Top <span className="text-[#00FF66]">1.7%</span> nationally.
                <br />
                <span className="text-[#A1A1AA]">Verifiable, independently scored.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-sm text-[#A1A1AA] leading-relaxed">
                Represented Santa Fe College in the National Cyber League Fall
                2022 Team Game — 9 domains, timed, adversarial. Our team ranked
                <span className="text-[#F5F5F5]"> 68th out of 3,926</span>.
                Full scouting report attached below.
              </p>
            </div>

            <a
              href={VERIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ncl-verify-cta"
              className="group inline-flex items-center gap-3 border border-[#00FF66] text-[#00FF66] px-5 py-3 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:bg-[#00FF66] hover:text-black w-max"
              style={{ transition: "background-color 0.15s, color 0.15s" }}
            >
              VERIFY REPORT
              <ExternalLink
                size={14}
                strokeWidth={1.5}
                className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                style={{ transition: "transform 0.15s" }}
              />
            </a>
          </div>
        </Reveal>

        <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" stagger={0.06}>
          {heroStats.map((s) => (
            <StaggerItem key={s.v}>
              <div
                data-testid={`ncl-hero-${s.v.toLowerCase()}`}
                className="border border-white/10 bg-[#0A0A0A] p-6 lg:p-8"
              >
                <div className="font-['Unbounded'] font-black text-3xl lg:text-4xl text-[#00FF66]">
                  {s.k}
                </div>
                <div className="mt-2 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA]">
                  {s.v}
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <div className="mt-10">
          <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-4">
            [DOMAIN_BREAKDOWN]
          </div>

          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" stagger={0.04}>
            {categories.map((c) => {
              const strong = c.accNum >= 70;
              return (
                <StaggerItem key={c.name}>
                  <div
                    data-testid={`ncl-cat-${c.name.toLowerCase().replace(/\W+/g, "-")}`}
                    className={`relative border p-5 flex flex-col gap-3 ${
                      strong
                        ? "border-[#00FF66]/40 bg-[#00FF66]/[0.03]"
                        : "border-white/10 bg-[#0A0A0A]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className={`font-['Unbounded'] font-bold text-sm lg:text-base ${strong ? "text-[#F5F5F5]" : "text-[#F5F5F5]/90"} leading-tight`}>
                        {c.name}
                      </div>
                      {strong && (
                        <span className="shrink-0 font-['JetBrains_Mono'] text-[8px] tracking-[0.25em] text-[#00FF66] border border-[#00FF66]/40 px-1.5 py-0.5">
                          STRONG
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10 font-['JetBrains_Mono']">
                      <div>
                        <div className="text-[9px] tracking-[0.3em] text-[#A1A1AA]">RANK</div>
                        <div className="mt-1 text-[#F5F5F5] text-base">{c.rank}</div>
                      </div>
                      <div>
                        <div className="text-[9px] tracking-[0.3em] text-[#A1A1AA]">ACCURACY</div>
                        <div className={`mt-1 text-base ${strong ? "text-[#00FF66]" : "text-[#F5F5F5]"}`}>
                          {c.acc}
                        </div>
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </div>
    </section>
  );
}
